/**
 * SB5-B.1 — TanStack Query hooks for the contracts feature.
 *
 * Hierarchical keys + per-query `staleTime`. Mirrors `useProjects.ts`
 * shape.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { projectsFetch } from "../projects/projectsFetch.js";
import { contractsKeys } from "./keys.js";
import { contractPositionsKeys } from "../contract-positions/keys.js";
import type {
  ContractCreateRequest,
  ContractDetail,
  ContractListResponse,
  ContractsFacetResponse,
  ContractUpdateRequest,
  ListContractsParams,
} from "./types.js";

function listContractsUrl(params: ListContractsParams): string {
  const sp = new URLSearchParams();
  sp.set("page", String(params.page ?? 1));
  sp.set("pageSize", String(params.pageSize ?? 50));
  if (params.q && params.q.length > 0) sp.set("q", params.q);
  if (params.projectIds && params.projectIds.length > 0) {
    sp.set("projectIds", params.projectIds.join(","));
  }
  if (params.siteIds && params.siteIds.length > 0) {
    sp.set("siteIds", params.siteIds.join(","));
  }
  if (params.customerIds && params.customerIds.length > 0) {
    sp.set("customerIds", params.customerIds.join(","));
  }
  if (params.contractorIds && params.contractorIds.length > 0) {
    sp.set("contractorIds", params.contractorIds.join(","));
  }
  if (params.states && params.states.length > 0) {
    sp.set("states", params.states.join(","));
  }
  sp.set("includeCompleted", params.includeCompleted ? "true" : "false");
  if (params.sortBy) sp.set("sortBy", params.sortBy);
  if (params.sortDir) sp.set("sortDir", params.sortDir);
  // TNCY-4 (2.4.0): scope=member fans the list out across granted
  // cross-tenant contracts. Only sent when explicitly `member` — omitting
  // it leaves the BE on its `own` default (no wire churn for own-only
  // callers).
  if (params.scope === "member") sp.set("scope", "member");
  return `/contracts?${sp.toString()}`;
}

export function useContractsListQuery(params: ListContractsParams) {
  return useQuery<ContractListResponse>({
    queryKey: contractsKeys.list(params),
    queryFn: () =>
      projectsFetch<ContractListResponse>(listContractsUrl(params)),
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * PM-1 — `POST /api/projects/v1/contracts` (`createContract`). Creates one
 * contract under a project (the unified-create flow mints one per park, with
 * the park's `siteId`). Returns the `ContractDetail` whose `contractId` links
 * the park's contract-positions. Mirrors `useCreateSiteMutation` /
 * `useCreateTurbineMutation` (projectsFetch, invalidate lists). Invalidates the
 * contracts + contract-positions lists so the new hierarchy surfaces after
 * navigation.
 */
export function useCreateContractMutation() {
  const qc = useQueryClient();
  return useMutation<ContractDetail, Error, ContractCreateRequest>({
    mutationFn: (body) =>
      projectsFetch<ContractDetail>("/contracts", { method: "POST", body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: contractsKeys.lists() });
    },
  });
}

/**
 * FB-43 (projects/v1) — `PATCH /api/projects/v1/contracts/{contractId}`
 * (`updateContract`). Updates a contract's party fields (customer/contractor
 * tenant OR contact, mutually exclusive) on the project-edit Parks step. The BE
 * runs the same auto-grant reconcile as `createContract` (tenant_admin-gated,
 * XT-D23 / PM-D23). On success it invalidates the contracts lists so the edited
 * party prefill re-resolves; the `contractId` arg keys the PATCH. The 200
 * returns the updated `ContractDetail`.
 */
export function useUpdateContractMutation() {
  const qc = useQueryClient();
  return useMutation<
    ContractDetail,
    Error,
    { contractId: string; body: ContractUpdateRequest }
  >({
    mutationFn: ({ contractId, body }) =>
      projectsFetch<ContractDetail>(
        `/contracts/${encodeURIComponent(contractId)}`,
        { method: "PATCH", body },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: contractsKeys.lists() });
    },
  });
}

/**
 * PMGT (projects/v1@2.12.0) — `DELETE /contracts/{contractId}`
 * (`deleteContract`). Soft-deletes a park (the project-edit "remove park"
 * action). The BE returns **409**
 * `{ code: conflict, reason: contract_has_positions }` when active contract
 * positions (turbines) still reference it — the caller surfaces a friendly
 * "can't remove — this park still has turbines" message from the thrown
 * `ProjectsFetchError`. On success (204) it invalidates the contracts lists
 * AND the contract-positions lists (the park's tree group goes away).
 */
export function useDeleteContractMutation() {
  const qc = useQueryClient();
  return useMutation<undefined, Error, string>({
    mutationFn: (contractId) =>
      projectsFetch<undefined>(`/contracts/${encodeURIComponent(contractId)}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: contractsKeys.lists() });
      void qc.invalidateQueries({ queryKey: contractPositionsKeys.lists() });
    },
  });
}

export function useContractsProjectsFacetQuery() {
  return useQuery<ContractsFacetResponse>({
    queryKey: contractsKeys.projectsFacet(),
    queryFn: () =>
      projectsFetch<ContractsFacetResponse>("/contracts/facets/projects"),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useContractsSitesFacetQuery() {
  return useQuery<ContractsFacetResponse>({
    queryKey: contractsKeys.sitesFacet(),
    queryFn: () =>
      projectsFetch<ContractsFacetResponse>("/contracts/facets/sites"),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useContractsCustomersFacetQuery() {
  return useQuery<ContractsFacetResponse>({
    queryKey: contractsKeys.customersFacet(),
    queryFn: () =>
      projectsFetch<ContractsFacetResponse>("/contracts/facets/customers"),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useContractsContractorsFacetQuery() {
  return useQuery<ContractsFacetResponse>({
    queryKey: contractsKeys.contractorsFacet(),
    queryFn: () =>
      projectsFetch<ContractsFacetResponse>("/contracts/facets/contractors"),
    staleTime: 5 * 60_000,
    retry: false,
  });
}
